import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addTmComment,
  completeTmTask,
  createTmTask,
  listTmComments,
  listTmTasks,
  updateTmTask,
  type CreateTmTaskInput,
  type UpdateTmTaskInput,
} from "../api/tmTasks";

export function useTmTasksList(enabled = true) {
  return useQuery({
    queryKey: ["tasks", "list"],
    queryFn: listTmTasks,
    staleTime: 30_000,
    enabled,
  });
}

export function useTmComments(taskId: string | null) {
  return useQuery({
    queryKey: ["tasks", "comments", taskId],
    queryFn: () => listTmComments(taskId as string),
    enabled: taskId !== null,
    staleTime: 15_000,
  });
}

function useInvalidateTasks() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["tasks"] });
}

export function useCreateTmTask() {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: (body: CreateTmTaskInput) => createTmTask(body),
    onSuccess: invalidate,
  });
}

export function useUpdateTmTask() {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: (args: { id: string; body: UpdateTmTaskInput }) =>
      updateTmTask(args.id, args.body),
    onSuccess: invalidate,
  });
}

export function useCompleteTmTask() {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: (id: string) => completeTmTask(id),
    onSuccess: invalidate,
  });
}

export function useAddTmComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { taskId: string; body: string }) =>
      addTmComment(args.taskId, args.body),
    onSuccess: (_r, args) =>
      qc.invalidateQueries({ queryKey: ["tasks", "comments", args.taskId] }),
  });
}
